import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";

export type PaginatedViewData = {
  order: number,
  key: ExportLabelView,
  predicate?: () => void,
  onEntry?: (fromIndex: number, toIndex: number) => void,
  onExit?: (fromIndex: number, toIndex: number) => void,
};

/**
 * Manages navigation between a set of paginated views. Provides mechanisms
 * to move between pages, retrieve page keys, and determine the navigational
 * state of the current page.
 */
export class PaginatedViewManager {
  private _currentPageIndex: number;
  private _pages: PaginatedViewData[];

  constructor(pages: PaginatedViewData[]) {
    this._pages = pages.sort((a, b) => a.order - b.order);
    this._currentPageIndex = 0;
  }

  public init() {
    if (this._pages[0].onEntry) {
      this._pages[0].onEntry(null, 0);
    }
  }

  private get _navigablePages(): PaginatedViewData[] {
    return this._pages.filter((page) => !page.predicate || page.predicate());
  }

  /**
   * Retrieves the total number of pages navigable via the current instance.
   *
   * @return {number} The total count of navigable pages.
   */
  public get pageCount(): number {
    return this._navigablePages.length;
  }

  /**
   * Retrieves the key for the current page being viewed.
   *
   * @return {ExportLabelView} The key associated with the current page*/
  public get currentPageKey(): ExportLabelView {
    return this._navigablePages[this._currentPageIndex].key;
  }

  /**
   * Determines if the current page is the first page.
   *
   * @return {boolean} True if the current page index is 0, otherwise false.
   */
  public get isFirstPage(): boolean {
    return this._currentPageIndex === 0;
  }

  /**
   * Determines if the current page is the last page.
   *
   * @return {boolean} True if the current page index is the last index, otherwise false.
   */
  public get isLastPage(): boolean {
    return this._currentPageIndex === this._navigablePages.length - 1;
  }

  /**
   * Advances to the next page if the current page is not the last.
   * Increments the current page index by one.
   * Does nothing if the current page is the last page.
   *
   * @return {void} No return value.
   */
  public next() {
    if (!this.isLastPage) {
      if (this._navigablePages[this._currentPageIndex].onExit) {
        this._navigablePages[this._currentPageIndex].onExit(this._currentPageIndex, this._currentPageIndex + 1);
      }

      this._currentPageIndex++;

      if (this._navigablePages[this._currentPageIndex].onEntry) {
        this._navigablePages[this._currentPageIndex].onEntry(this._currentPageIndex - 1, this._currentPageIndex);
      }
    }
  }

  /**
   * Navigates to the previous page if the current page is not the first page.
   *
   * @return {void} No return value.
   */
  public previous() {
    if (!this.isFirstPage) {
      if (this._navigablePages[this._currentPageIndex].onExit) {
        this._navigablePages[this._currentPageIndex].onExit(this._currentPageIndex, this._currentPageIndex - 1);
      }

      this._currentPageIndex--;

      if (this._navigablePages[this._currentPageIndex].onEntry) {
        this._navigablePages[this._currentPageIndex].onEntry(this._currentPageIndex + 1, this._currentPageIndex);
      }
    }
  }
}
